class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    // Mantener referencia al estado para accesibilidad
    this.statusElement = this.querySelector('.predictive-search-status');

    // Asegurar que el portal existe
    this.ensurePortalExists();

    // Inicializar referencia a resultados
    this.predictiveSearchResults = document.querySelector('#predictive-search-portal [data-predictive-search]');

    this.setupEventListeners();
  }

  ensurePortalExists() {
    let portalContainer = document.getElementById('predictive-search-portal');

    if (!portalContainer) {
      // Crear el portal si no existe
      portalContainer = document.createElement('div');
      portalContainer.id = 'predictive-search-portal';

      const searchResultsDiv = document.createElement('div');
      searchResultsDiv.className = 'predictive-search predictive-search--header';
      searchResultsDiv.setAttribute('tabindex', '-1');
      searchResultsDiv.setAttribute('data-predictive-search', '');

      // Añadir el loading spinner al portal
      const loadingSpinner = document.createElement('div');
      loadingSpinner.className = 'predictive-search__loading-state';
      loadingSpinner.innerHTML = '<div class="spinner"></div>';
      searchResultsDiv.appendChild(loadingSpinner);

      portalContainer.appendChild(searchResultsDiv);
      document.body.appendChild(portalContainer);

      // Agregar eventos para mantener la interactividad del portal
      portalContainer.addEventListener('mousedown', (event) => {
        // Prevenir que el evento se propague y active onFocusOut
        event.stopPropagation();
      });

      // Referencia al contexto actual para usarlo en el evento
      const self = this;

      // Capturar los clics en elementos dentro del portal
      portalContainer.addEventListener('click', (event) => {
        // Si el clic fue en el botón "Buscar"
        if (event.target.closest('.predictive-search__item--term')) {
          event.preventDefault();
          const button = event.target.closest('.predictive-search__item--term');
          const searchTermText = button.querySelector('[data-predictive-search-search-for-text]');

          // Acceder a todos los componentes de búsqueda predictiva
          document.querySelectorAll('predictive-search').forEach(searchComponent => {
            if (searchTermText) {
              const searchText = searchTermText.textContent;
              const searchTerm = searchText.replace(/^.*(search for|buscar)\s+(.*)$/i, '$2').trim();

              // Actualizar el valor del input en el componente de búsqueda
              const searchInput = searchComponent.querySelector('input[type="search"]');
              if (searchInput) {
                searchInput.value = searchTerm;
                searchInput.form.submit();
              }
            }
          });
        }
      });
    }
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Agregar manejadores para scroll y resize
    this.scrollHandler = this.handleViewportChange.bind(this);
    this.resizeHandler = this.handleViewportChange.bind(this);

    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    window.addEventListener('resize', this.resizeHandler, { passive: true });
  }

  handleViewportChange() {
    if (!this.isOpen) return;

    const portalContainer = document.getElementById('predictive-search-portal');
    if (!portalContainer) return;

    // Verificar si el input está visible
    if (this.isElementInViewport(this.input)) {
      // Actualizar posición del portal cuando el input es visible
      const searchInputRect = this.input.getBoundingClientRect();
      portalContainer.style.position = 'fixed';
      portalContainer.style.top = `${searchInputRect.bottom + window.scrollY}px`;
      portalContainer.style.left = `${searchInputRect.left + window.scrollX}px`;
      portalContainer.style.width = `${searchInputRect.width}px`;
      portalContainer.classList.remove('predictive-search-out-of-view');
    } else {
      // Ocultar el portal cuando el input no es visible
      portalContainer.classList.add('predictive-search-out-of-view');
    }
  }

  isElementInViewport(el) {
    const rect = el.getBoundingClientRect();

    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      // Verificar si el documento activo está dentro del componente original o dentro del portal
      const portalContainer = document.getElementById('predictive-search-portal');
      const clickedInsidePortal = portalContainer && portalContainer.contains(document.activeElement);

      // Solo cerrar si el clic no está ni en el componente ni en el portal
      if (!this.contains(document.activeElement) && !clickedInsidePortal) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) {
      if (selectedOption.classList.contains('predictive-search__item--term')) {
        const searchTermText = selectedOption.querySelector('[data-predictive-search-search-for-text]');
        if (searchTermText) {
          // Extraer el término de búsqueda del texto y establecerlo en el input
          const searchText = searchTermText.textContent;
          // Usar una expresión regular que funcione para "search for X" y "buscar X"
          const searchTerm = searchText.replace(/^.*(search for|buscar)\s+(.*)$/i, '$2').trim();
          this.input.value = searchTerm;
        }
        // Enviar el formulario
        this.input.form.submit();
      } else {
        // Para otros elementos, simplemente hacer clic
        selectedOption.click();
      }
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);

    // Sincronizar el estado de carga con el portal
    const portalContainer = document.getElementById('predictive-search-portal');
    if (portalContainer) {
      portalContainer.setAttribute('loading', true);
    }
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    // Añadir manejador de evento para el botón "Buscar"
    const searchButton = this.predictiveSearchResults.querySelector('.predictive-search__item--term');
    if (searchButton) {
      searchButton.addEventListener('click', (event) => {
        event.preventDefault();
        const searchTermText = searchButton.querySelector('[data-predictive-search-search-for-text]');
        if (searchTermText) {
          // Extraer el término de búsqueda y enviarlo
          const searchText = searchTermText.textContent;
          // Usar una expresión regular que funcione para "search for X" y "buscar X"
          const searchTerm = searchText.replace(/^.*(search for|buscar)\s+(.*)$/i, '$2').trim();
          this.input.value = searchTerm;
        }
        this.input.form.submit();
      });
    }

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    // Sincronizar el estado de carga con el portal
    const portalContainer = document.getElementById('predictive-search-portal');
    if (portalContainer) {
      portalContainer.removeAttribute('loading');
    }

    if (this.querySelector('[data-predictive-search-live-region-count-value]')) {
      this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.predictiveSearchResults) return;

    // Posicionar el portal justo debajo del campo de búsqueda
    const searchInputRect = this.input.getBoundingClientRect();
    const portalContainer = document.getElementById('predictive-search-portal');

    // Calcular la posición óptima
    portalContainer.style.position = 'fixed';
    portalContainer.style.top = `${searchInputRect.bottom + window.scrollY}px`;
    portalContainer.style.left = `${searchInputRect.left + window.scrollX}px`;
    portalContainer.style.width = `${searchInputRect.width}px`;
    portalContainer.style.zIndex = '1000'; // Asegura que esté por encima de otros elementos
    portalContainer.style.display = 'block';
    portalContainer.classList.add('is-open');
    portalContainer.classList.remove('predictive-search-out-of-view');

    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    // Verificar si el input es visible inmediatamente después de abrir
    this.handleViewportChange();
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    // Ocultar el portal
    const portalContainer = document.getElementById('predictive-search-portal');
    if (portalContainer) {
      portalContainer.style.display = 'none';
      portalContainer.classList.remove('is-open'); // Add this line
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute('style');
    }
  }

  // Agregar esto como nuevo método
  disconnectedCallback() {
    window.removeEventListener('scroll', this.scrollHandler);
    window.removeEventListener('resize', this.resizeHandler);
  }
}

customElements.define('predictive-search', PredictiveSearch);

const style = document.createElement('style');
style.textContent = `
#predictive-search-portal {
  display: none;
  position: fixed;
  z-index: 1000;
  left: 0;
  top: 0;
}

#predictive-search-portal.predictive-search-out-of-view {
  display: none !important;
}

#predictive-search-portal .predictive-search {
  background-color: #fff;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  width: 100%;
  overflow-y: auto;
}
`;
document.head.appendChild(style);
